import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getMe } from "../services/authService";
import { AuthUser } from "../types/auth";

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  setUser: (user: AuthUser | null) => void;
  hasPermission: (permission: string) => boolean;
  canEdit: boolean;
  canViewWorkload: boolean;
  canManageUsers: boolean;
  canCreateComments: boolean;
  canViewComments: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    getMe().then((sessionUser) => {
      setUser(sessionUser);
      setLoading(false);
    });
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const hasPermission = (permission: string) =>
      Boolean(user?.permissions?.includes(permission));

    return {
      user,
      loading,
      setUser,
      hasPermission,
      canEdit: hasPermission("schedule.write"),
      canViewWorkload: hasPermission("workload.view"),
      canManageUsers: hasPermission("admin.access"),
      canCreateComments: hasPermission("comments.create"),
      canViewComments: hasPermission("comments.view"),
    };
  }, [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
export function useAuthContext(): AuthContextValue {
  return useAuth();
}